// src/pages/ContentEditor/components/tabs/ComprehensionTab.js
import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  IconButton, 
  TextField, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  alpha 
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const ComprehensionTab = ({ questions, onNestedChange, onAddItem, onRemoveItem }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="text.primary">
          Comprehension Questions
        </Typography>
        
        <Button
          startIcon={<AddIcon />}
          onClick={onAddItem}
          sx={{ color: theme.palette.primary.main }}
        >
          Add Question
        </Button>
      </Box>
      
      {questions.map((question, qIndex) => (
        <Card 
          key={qIndex} 
          variant="outlined" 
          sx={{ 
            mb: 3, 
            backgroundColor: theme.palette.background.paper,
            borderColor: alpha(theme.palette.common.white, 0.1),
            '&:hover': {
              borderColor: theme.palette.primary.main,
            }
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={500} color="text.primary">
                Question {qIndex + 1}
              </Typography>
              
              <IconButton 
                color="error" 
                onClick={() => onRemoveItem(qIndex)}
                disabled={questions.length <= 1}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
            
            <TextField
              fullWidth
              label="Question Text"
              variant="outlined"
              placeholder="Enter your question..."
              value={question.questionText.text}
              onChange={(e) => onNestedChange('comprehensionActivity', qIndex, 'question', e.target.value)}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: alpha(theme.palette.common.white, 0.2),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha(theme.palette.common.white, 0.3),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  backgroundColor: alpha(theme.palette.common.white, 0.05)
                }
              }}
              InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
              InputProps={{ style: { color: theme.palette.text.primary } }}
            />
            
            <Typography variant="subtitle2" gutterBottom color="text.primary">
              Answer Options
            </Typography>
            
            <Grid container spacing={2}>
              {question.options.map((option, oIndex) => (
                <Grid item xs={12} key={oIndex}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      fullWidth
                      label={`Option ${String.fromCharCode(65 + oIndex)}`}
                      variant="outlined"
                      value={option.text}
                      onChange={(e) => onNestedChange(
                        'comprehensionActivity', 
                        qIndex, 
                        `option-${oIndex}`, 
                        e.target.value
                      )}
                      InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
                      InputProps={{ style: { color: theme.palette.text.primary } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alpha(theme.palette.common.white, 0.2),
                          },
                          '&:hover fieldset': {
                            borderColor: alpha(theme.palette.common.white, 0.3),
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main,
                          },
                          backgroundColor: alpha(theme.palette.common.white, 0.05)
                        }
                      }}
                    />
                    
                    <FormControl 
                      sx={{ 
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: alpha(theme.palette.common.white, 0.2),
                          },
                          '&:hover fieldset': {
                            borderColor: alpha(theme.palette.common.white, 0.3),
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main,
                          },
                          backgroundColor: alpha(theme.palette.common.white, 0.05)
                        }
                      }}
                    >
                      <InputLabel 
                        id={`correct-answer-${qIndex}-${oIndex}-label`}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Correct?
                      </InputLabel>
                      <Select
                        labelId={`correct-answer-${qIndex}-${oIndex}-label`}
                        value={question.correctIndex === oIndex ? 'yes' : 'no'}
                        label="Correct?"
                        onChange={(e) => {
                          if (e.target.value === 'yes') {
                            onNestedChange('comprehensionActivity', qIndex, 'correctIndex', oIndex);
                          }
                        }}
                        sx={{ color: theme.palette.text.primary }}
                      >
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default ComprehensionTab;